import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { ArrowRightOutlined } from '@ant-design/icons';
import Modal from 'antd/lib/modal/Modal';

import { AuthActions } from '@portal/actions';
import { getSettingsRemote } from '@portal/actions/onboarding';
import { myAccount } from '@portal/config/app.json';
import { getRouteStackPath } from '@portal/config/routes';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import {
  ACTION_TYPES,
  UserCredentialType,
} from '@portal/models/enumerators/auth';
import { translate } from '@portal/services/i18n';
import { hasAccess } from '@portal/utils/hasAccess';

import FaqIcon from '@portal/assets/svg/faq.svg?react';
import CloseAccount from '@portal/assets/svg/my-account/ic_close-account.svg?react';
import Config from '@portal/assets/svg/my-account/ic_config.svg?react';
import Devices from '@portal/assets/svg/my-account/ic_devices.svg?react';
import IncomeTax from '@portal/assets/svg/my-account/ic_income-tax.svg?react';
import MultiAccess from '@portal/assets/svg/my-account/ic_multiaccess.svg?react';
import Password from '@portal/assets/svg/my-account/ic_password.svg?react';
import PayStub from '@portal/assets/svg/my-account/ic_paystub.svg?react';
import PIN from '@portal/assets/svg/my-account/ic_PIN.svg?react';
import Profile from '@portal/assets/svg/my-account/ic_profile.svg?react';
import Salary from '@portal/assets/svg/my-account/ic_salary.svg?react';
import UseTerm from '@portal/assets/svg/my-account/ic_use-term.svg?react';
import PixLimit from '@portal/assets/svg/pix-buttons/ic_money-off.svg?react';

import AccessDeniedModal from '@portal/components/AccessDeniedModal/AccessDeniedModal';
import CloseAccountModal from '@portal/components/CloseAccountModal/CloseAccountModal';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import Loading from '@portal/components/Loading/Loading';
import TokenMethodSelectionModal from '@portal/components/TokenMethodSelectionModal/TokenMethodSelectionModal';
import useReduxState from '@portal/hooks/useReduxState';
import { TermsTypes } from '@portal/models/enumerators/onboarding';

import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';

import RolesType from '@portal/enum/rolesType';
import { useNavigation } from '@portal/hooks/useNavigation';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { getPaymentAccountByType } from '@portal/utils/paymentAccount';

const MyAccountPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const ActionType = {
    pin: ACTION_TYPES.PIN,
    password: ACTION_TYPES.PASSWORD,
  };

  const {
    loading: { amount: isLoading },
    auth: { changeUserCredential, changeUserCredentialFail },
    user: { roles },
    onboarding: { terms },
    user: {
      me: {
        account: { type },
      },
      me: loggedUser,
    },
    settings: { keys },
    account: { config },
    theme: { currentTheme },
  } = useReduxState();

  const hasDeviceAccess = hasAccess(roles, [
    RolesType.MY_ACCOUNT,
    RolesType.DEVICE_MANAGER,
  ]);

  useEffect(() => {
    if (terms.length === 0) {
      dispatch(getSettingsRemote());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTermByType = (type: string) => {
    if (
      type === TermsTypes.PJ_TARIFF_TABLE ||
      type === TermsTypes.PF_TARIFF_TABLE
    ) {
      const key = keys?.find((key) => key[type]);
      return key ? key[type] : ({ token: '', type, url: '' } as models.Terms);
    } else {
      const term = terms.find((term) => term.type === type);
      return term ?? ({ token: '', type, url: '' } as models.Terms);
    }
  };

  const paymentAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.ACCOUNT
  );

  const escrowAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.ESCROW
  );

  const loanAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.LOAN
  );

  const hideEscrowOptions = () => {
    if (!paymentAccount && escrowAccount) return true;

    return false;
  };

  const hideLoanOptions = () => {
    if (!paymentAccount && loanAccount) return true;

    return false;
  };

  const hideLimitOption = (): boolean => {
    return (
      !myAccount.accountLimits ||
      !hasAccess(roles, [
        RolesType.READ_PIX_LIMIT,
        RolesType.READ_BILLET_LIMIT,
        RolesType.READ_TED_LIMIT,
        RolesType.READ_TEF_LIMIT,
        RolesType.MANAGE_BILLET_LIMIT,
        RolesType.MANAGE_PIX_LIMIT,
        RolesType.MANAGE_TED_LIMIT,
        RolesType.MANAGE_TEF_LIMIT,
      ])
    );
  };

  const actions = [
    {
      icon: <Profile />,
      title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
      onClick: () =>
        navigate(getRouteStackPath('PROFILE', 'PROFILE_INFORMATION')),
      hidden: !myAccount.profileInformation,
      id: 'PROFILE',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.ACCOUNT'),
    },
    {
      icon: <Devices />,
      title: translate('PAGES.MY_ACCOUNT.TABS.MY_DEVICES'),
      onClick: () => navigate(getRouteStackPath('PROFILE', 'MY_DEVICES')),
      hidden: !myAccount.profileInformation && !hasDeviceAccess,
      id: 'MY_DEVICES',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.MY_DEVICES'),
    },
    {
      icon: <Password />,
      title: translate('PAGES.MY_ACCOUNT.TABS.CREDENTIALS.CHANGE_PASSWORD'),
      onClick: () => {
        setActionType(ActionType.password);
        setMethodSelectionModal(true);
      },
      hidden: !myAccount.changePassword,
      id: 'CHANGE_PASSWORD',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.CREDENTIALS'),
    },
    {
      icon: <PIN />,
      title: translate('PAGES.MY_ACCOUNT.TABS.CHANGE_PIN'),
      onClick: () => {
        setActionType(ActionType.pin);
        setMethodSelectionModal(true);
      },
      id: 'CHANGE_PIN',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.CHANGE_PIN'),
    },
    {
      icon: <Password />,
      title: translate('PAGES.MY_ACCOUNT.TABS.CREDENTIALS.CHANGE_PASSWORD_2'),
      text: translate('PAGES.MY_ACCOUNT.TABS.CREDENTIALS.CHANGE_PASSWORD_TEXT'),
      hidden: !myAccount.passwordInformationMenu,
      id: 'CHANGE_PASSWORD_2',
    },
    {
      icon: <IncomeTax />,
      title: translate('PAGES.MY_ACCOUNT.TABS.INCOME_TAX'),
      onClick: () => navigate(getRouteStackPath('PROFILE', 'INCOME_TAX')),
      hidden: !myAccount.incomeTax || hideEscrowOptions() || hideLoanOptions(),
      id: 'INCOME_TAX',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.INCOME_TAX'),
    },
    {
      icon: <PayStub />,
      title: translate('PAGES.MY_ACCOUNT.PAY_STUB.PAGE_TITLE'),
      onClick: () => {
        if (hasAccess(roles, RolesType.ACCOUNT_READ)) {
          navigate(getRouteStackPath('PROFILE', 'PAY_STUB'));
        } else {
          setHasAccessModal(true);
        }
      },
      hidden: !myAccount.payStub || hideEscrowOptions() || hideLoanOptions(),
      id: 'PAY_STUB',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.PAY_STUB'),
    },
    {
      icon: <Salary />,
      title: translate('PAGES.MY_ACCOUNT.TABS.SALARY_PORTABILITY'),
      onClick: () => {
        if (
          myAccount?.validatePortabilityStatus &&
          !loggedUser.enablePortability
        ) {
          setHasAccessModal(true);
          return;
        }

        navigate(getRouteStackPath('PROFILE', 'SALARY_PORTABILITY'));
      },
      hidden:
        !(
          (type === AccountTaxIdentifierType.PERSON &&
            myAccount.salaryPortabilityPF) ||
          (type === AccountTaxIdentifierType.CORPORATE &&
            myAccount.salaryPortabilityPJ)
        ) ||
        hideEscrowOptions() ||
        hideLoanOptions(),
      id: 'SALARY_PORTABILITY',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.SALARY_PORTABILITY'),
    },
    {
      icon: <UseTerm />,
      title: translate('PAGES.MY_ACCOUNT.TABS.TERMS_OF_USE'),
      onClick: () => {
        setModalTerms({
          visible: true,
          type: TermsTypes.TERMS_OF_USE,
        });
      },
      hidden: !myAccount.termsOfUse,
      id: 'TERMS_OF_USE',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.TERMS_OF_USE'),
    },
    {
      icon: <UseTerm />,
      title: translate('PAGES.MY_ACCOUNT.TABS.TARIFF_TABLE'),
      onClick: () => {
        setModalTerms({
          visible: true,
          type:
            type === AccountTaxIdentifierType.PERSON
              ? TermsTypes.PF_TARIFF_TABLE
              : TermsTypes.PJ_TARIFF_TABLE,
        });
      },
      hidden: !myAccount.allowTariffAccountTable,
      id: 'TARIFF_TABLE',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.TARIFF_TABLE'),
    },
    {
      icon: <UseTerm />,
      title: translate('PAGES.MY_ACCOUNT.TABS.PRIVACY_POLICY'),
      onClick: () => {
        setModalTerms({
          visible: true,
          type: TermsTypes.PRIVACY_POLICY,
        });
      },
      hidden: !myAccount.privacyPolicy,
      id: 'PRIVACY_POLICY',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.PRIVACY_POLICY'),
    },
    {
      icon: <MultiAccess />,
      title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE'),
      onClick: () => {
        if (
          hasAccess(roles, [
            RolesType.ACCOUNT_READ,
            RolesType.ACCOUNT_ADD_USER,
            RolesType.MANAGE_ACCOUNTS,
          ])
        ) {
          navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'));
        } else {
          setHasAccessModal(true);
        }
      },
      hidden:
        !(
          (type === AccountTaxIdentifierType.PERSON &&
            myAccount.multiAccessPf) ||
          (type === AccountTaxIdentifierType.CORPORATE &&
            myAccount.multiAccessPj)
        ) ||
        hideEscrowOptions() ||
        hideLoanOptions(),
      id: 'MULTI_ACCESS',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.MULTI_ACCESS'),
    },
    {
      icon: <PixLimit />,
      title: translate('PAGES.LIMITS.LIMITS_HEADER'),
      onClick: () => {
        navigate(getRouteStackPath('LIMITS', 'LIMITS_PAGE'));
      },
      hidden: hideLimitOption(),
      id: 'LIMITS',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.MY_LIMITS'),
    },
    {
      icon: <Config />,
      title: translate('PAGES.MY_ACCOUNT.TABS.CONFIGURATION'),
      onClick: () => {
        if (hasAccess(roles, RolesType.MY_ACCOUNT)) {
          navigate(getRouteStackPath('PROFILE', 'CONFIGURATION'));
        } else {
          setHasAccessModal(true);
        }
      },
      hidden:
        !!(config?.length === 0) ||
        !myAccount.settings ||
        type === AccountTaxIdentifierType.PERSON,
      id: 'CONFIGURATION',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.CONFIGURATION'),
    },
    {
      icon: <FaqIcon />,
      title: translate('PAGES.MY_ACCOUNT.TABS.FAQ'),
      onClick: () => navigate(getRouteStackPath('PROFILE', 'FAQ')),
      hidden: !myAccount.faq,
      id: 'FAQ',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.FAQ'),
    },
    {
      icon: <CloseAccount />,
      title: translate('PAGES.MY_ACCOUNT.TABS.CLOSE_ACCOUNT'),
      onClick: () => {
        if (hasAccess(roles, RolesType.ACCOUNT_READ)) {
          setModalCloseAccount(true);
        } else {
          setHasAccessModal(true);
        }
      },
      hidden: !myAccount.closeAccount,
      id: 'CLOSE_ACCOUNT',
      text: translate('PAGES.MY_ACCOUNT.DESCRIPTIONS.CLOSE_ACCOUNT'),
    },
  ];

  const [code, setCode] = useState('');
  const [actionType, setActionType] = useState('');
  const [newPinModal, setNewPinModal] = useState(false);
  const [methodSelectionModal, setMethodSelectionModal] = useState(false);

  const [modalTerms, setModalTerms] = useState({
    visible: false,
    type: TermsTypes.TERMS_OF_USE,
  });
  const [modalCloseAccount, setModalCloseAccount] = useState(false);
  const [hasAccessModal, setHasAccessModal] = useState(false);

  const handleConfirmCode = (newCode: string) => {
    setCode(newCode);
    if (actionType === ActionType.password) {
      navigate(getRouteStackPath('PROFILE', 'ALTER_PASSWORD'), {
        state: newCode,
      });
    } else {
      setNewPinModal(true);
    }
  };

  const handleNewPin = (pin: string) => {
    const requestData: models.UpdateUserCredentialRequest = {
      type: UserCredentialType.PIN,
      credential: pin,
      token: code,
    };

    setNewPinModal(false);
    dispatch(AuthActions.userCredentialChangeCredential(requestData));
  };

  useEffect(() => {
    if (changeUserCredentialFail) {
      setNewPinModal(false);
      dispatch(AuthActions.userCredentialError(null));
    }
  }, [changeUserCredentialFail, dispatch]);

  useEffect(() => {
    if (changeUserCredential) {
      setNewPinModal(false);
      navigate(getRouteStackPath('PROFILE', 'PROFILE_PAGE'));

      dispatch(AuthActions.userCredentialSuccess(false));
    }
  }, [changeUserCredential, dispatch, history]);

  const visibleActions = actions.filter((action) => !action.hidden);

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: !!paymentAccount || !!escrowAccount,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.MY_ACCOUNT'),
          },
        ]}
      />
      <div className="my-account-page">
        <AdvancedPanelContentHeader
          title={translate('PAGES.MY_ACCOUNT.TABS.MY_ACCOUNT')}
          description={
            myAccount.description
              ? translate('PAGES.MY_ACCOUNT.TABS.MY_ACCOUNT_DESCRIPTION')
              : translate('PAGES.MY_ACCOUNT.TABS.DESCRIPTION')
          }
          testId="my-account-panel-header"
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
          buttonTitle={translate('APPLICATION.BACK_TO_INIT')}
        />

        <div className="my-account-page__options">
          {visibleActions.map((action) => (
            <div
              key={action.id}
              className={`my-account-page__action-card ${
                !action.onClick ? 'my-account-page__action-card--disabled' : ''
              }`}
              style={{ borderTop: `2px solid ${currentTheme.borderColor}` }}
              role={action.onClick ? 'button' : undefined}
              tabIndex={0}
              onClick={action.onClick}
              data-testid={`my-account_${action.id}_click`}
            >
              <div className="my-account-page__action-content">
                <div
                  className="my-account-page__action-icon"
                  style={{ background: currentTheme.background }}
                >
                  {action.icon}
                </div>
                <div className="my-account-page__action-text">
                  <ThemedText
                    variant="h3"
                    className="my-account-page__action-title"
                  >
                    {action.title}
                  </ThemedText>
                  {action.text ? (
                    <ThemedText
                      variant="body"
                      className="my-account-page__action-description"
                    >
                      {action.text}
                    </ThemedText>
                  ) : null}
                </div>
                {action.onClick ? (
                  <ArrowRightOutlined className="my-account-page__action-arrow" />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
      {isLoading > 0 && <Loading />}
      <TokenMethodSelectionModal
        visible={methodSelectionModal}
        setVisible={setMethodSelectionModal}
        onConfirmCode={handleConfirmCode}
        title={translate(
          actionType === ActionType.password
            ? 'PAGES.MY_ACCOUNT.TABS.CHANGE_PASSWORD_STACK'
            : 'PAGES.MY_ACCOUNT.TABS.CHANGE_PIN'
        )}
      />
      {modalCloseAccount ? (
        <CloseAccountModal
          visible={modalCloseAccount}
          onCloseModal={() => setModalCloseAccount(false)}
        />
      ) : null}
      <ConfirmationModal
        visible={newPinModal}
        setVisible={setNewPinModal}
        handleConfirmation={handleNewPin}
        title={translate(
          'PAGES.MY_ACCOUNT.TABS.CREDENTIALS.CHANGE_CREDENTIALS'
        )}
        text=" "
      />
      <AccessDeniedModal
        setVisible={setHasAccessModal}
        visible={hasAccessModal}
      />
      <Modal
        open={modalTerms.visible}
        footer={null}
        title={null}
        closable
        onCancel={() =>
          setModalTerms({
            visible: false,
            type: TermsTypes.TERMS_OF_USE,
          })
        }
        width={1000}
      >
        <ThemedText variant="body" className="terms-of-use__title">
          {translate(
            `PAGES.MY_ACCOUNT.TABS.${
              modalTerms.type === TermsTypes.TERMS_OF_USE
                ? 'TERMS_OF_USE'
                : modalTerms.type === TermsTypes.PF_TARIFF_TABLE ||
                  modalTerms.type === TermsTypes.PJ_TARIFF_TABLE
                ? 'TARIFF_TABLE'
                : 'PRIVACY_POLICY'
            }`
          )}
        </ThemedText>
        <iframe
          src={
            modalTerms.type === TermsTypes.PF_TARIFF_TABLE ||
            modalTerms.type === TermsTypes.PJ_TARIFF_TABLE
              ? `${getTermByType(modalTerms.type)}#toolbar=0`
              : `${getTermByType(modalTerms.type).url}#toolbar=0`
          }
          width="100%"
          height={500}
        />
      </Modal>
    </>
  );
};

export default MyAccountPage;
